import React, { useEffect, useState } from "react";
import "./visaTerms.css";
import usePageMeta from "../../../hooks/usePageMeta";

const visaTerms = [
  {
    number: 1,
    title: "Visa Assistance Services",
    paragraphs: [
      "MakeBeeTrip may assist customers with services such as:",
    ],
    bullets: [
      "Visa application guidance",
      "Document checklist and requirements",
      "Application form assistance",
      "Document collection and submission support",
      "Appointment assistance, where applicable",
      "Application status assistance",
      "Coordination with relevant visa processing partners",
      "General travel and visa-related guidance",
    ],
    after: [
      "The exact services provided may vary depending on the destination, visa category and applicable requirements.",
    ],
  },
  {
    number: 2,
    title: "Visa Approval Is Not Guaranteed",
    paragraphs: [
      "MakeBeeTrip does not guarantee visa approval.",
      "The final decision to approve, refuse, delay or otherwise process a visa application is solely made by the relevant:",
    ],
    bullets: [
      "Embassy",
      "Consulate",
      "Immigration authority",
      "Visa application centre",
      "Government authority",
      "Other authorized visa-issuing authority",
    ],
    after: [
      "Payment of visa fees or service charges does not guarantee the issuance of a visa.",
    ],
  },
  {
    number: 3,
    title: "Visa Processing Time",
    paragraphs: [
      "Visa processing times are provided for general guidance only.",
      "Processing may take longer than the estimated time due to:",
    ],
    bullets: [
      "Embassy or consulate workload",
      "Additional document requirements",
      "Background or security checks",
      "Public holidays",
      "Administrative processing",
      "Verification requirements",
      "Changes in government procedures",
      "Technical or operational issues",
      "Other circumstances beyond our control",
    ],
    after: [
      "MakeBeeTrip does not guarantee a specific visa processing time.",
      "Customers are strongly advised to apply sufficiently in advance of their intended travel date.",
    ],
  },
  {
    number: 4,
    title: "Customer Responsibility",
    paragraphs: [
      "Customers are responsible for providing complete, accurate and genuine information and documents.",
      "Depending on the visa type, this may include:",
    ],
    bullets: [
      "Valid passport",
      "Passport photographs",
      "Travel itinerary",
      "Hotel booking details",
      "Flight information",
      "Bank or financial documents",
      "Employment documents",
      "Invitation letters",
      "Travel insurance",
      "Previous travel information",
      "Supporting documents",
      "Other documents required by the relevant authority",
    ],
    after: [
      "Providing incomplete, inaccurate, misleading or false information may result in visa refusal, cancellation or other consequences.",
    ],
  },
  {
    number: 5,
    title: "Passport Requirements",
    paragraphs: [
      "Customers are responsible for ensuring that their passport:",
    ],
    bullets: [
      "Is valid for the required period",
      "Contains sufficient blank pages where applicable",
      "Is not damaged",
      "Meets the destination country's requirements",
    ],
    after: [
      "Passport requirements may differ depending on the destination and visa category.",
    ],
  },
  {
    number: 6,
    title: "Visa Fees and Service Charges",
    paragraphs: [
      "Visa-related payments may include:",
    ],
    bullets: [
      "Government/embassy visa fees",
      "Visa application centre charges",
      "MakeBeeTrip service charges",
      "Courier charges",
      "Appointment charges, where applicable",
      "Documentation charges, where applicable",
      "Other applicable third-party charges",
    ],
    after: [
      "Government, embassy, consulate and visa centre fees may be non-refundable, even if the visa application is refused, withdrawn or cancelled.",
      "MakeBeeTrip service charges may also be non-refundable depending on the stage of processing and the applicable booking/service terms.",
    ],
  },
  {
    number: 7,
    title: "Visa Rejection",
    paragraphs: [
      "If a visa application is rejected or refused by the relevant authority, MakeBeeTrip is not responsible for the decision.",
      "Visa rejection does not automatically entitle the customer to a refund of all amounts paid.",
      "Any refund, if applicable, will be determined according to the specific visa service terms and the stage of processing.",
    ],
  },
  {
    number: 8,
    title: "Additional Documents",
    paragraphs: [
      "The embassy, consulate, immigration authority or visa processing centre may request additional documents or information at any stage.",
      "Customers must provide requested information within the required timeframe.",
      "Failure to provide additional documents may result in delays, refusal or cancellation of the application.",
    ],
  },
  {
    number: 9,
    title: "Visa Interview",
    paragraphs: [
      "Some visa categories or destinations may require a personal interview, biometric appointment or additional verification.",
      "Customers are responsible for attending appointments at the specified date and time.",
      "MakeBeeTrip may assist with appointment-related information where applicable but does not control the decision or outcome of the interview.",
    ],
  },
  {
    number: 10,
    title: "Biometrics",
    paragraphs: [
      "Certain visa applications require biometric information such as fingerprints or photographs.",
      "Where biometric submission is required, the customer must personally attend the relevant centre or appointment unless the applicable authority provides an exemption.",
    ],
  },
  {
    number: 11,
    title: "Changes in Visa Rules",
    paragraphs: [
      "Visa requirements, fees, processing times and documentation requirements may change at any time.",
      "Such changes may be made by the relevant government, embassy, consulate or immigration authority without prior notice.",
      "MakeBeeTrip will make reasonable efforts to provide updated information, but customers should verify the latest requirements before travelling.",
    ],
  },
  {
    number: 12,
    title: "Immigration and Entry",
    paragraphs: [
      "A visa does not automatically guarantee entry into a country.",
      "Final admission and entry decisions may be made by immigration authorities at the port of entry.",
      "Customers may be required to provide additional documents or satisfy entry requirements upon arrival.",
      "MakeBeeTrip is not responsible for denial of entry by immigration authorities.",
    ],
  },
  {
    number: 13,
    title: "Transit Visa",
    paragraphs: [
      "Customers travelling through another country may require a transit visa even if they are not leaving the airport.",
      "Transit visa requirements depend on:",
    ],
    bullets: [
      "Passport nationality",
      "Route",
      "Airport",
      "Airline",
      "Transit duration",
      "Visa status",
      "Destination",
      "Applicable immigration rules",
    ],
    after: [
      "Customers are responsible for checking transit visa requirements applicable to their itinerary.",
    ],
  },
  {
    number: 14,
    title: "Accuracy of Passenger Information",
    paragraphs: [
      "Customers must carefully verify all information submitted for visa processing.",
      "This includes:",
    ],
    bullets: [
      "Full name",
      "Passport number",
      "Date of birth",
      "Nationality",
      "Passport issue and expiry dates",
      "Travel dates",
      "Previous travel information",
      "Employment information",
      "Other application details",
    ],
    after: [
      "Errors may result in additional charges, delays, rejection or the need to submit a new application.",
    ],
  },
  {
    number: 15,
    title: "Fraudulent or False Documents",
    paragraphs: [
      "MakeBeeTrip does not support or assist with the submission of false, forged, altered or misleading documents.",
      "Customers must provide genuine and authentic documents.",
      "If fraudulent or false documents are identified, the application may be cancelled and the matter may be referred to the relevant authority where required.",
    ],
  },
  {
    number: 16,
    title: "Cancellation of Visa Services",
    paragraphs: [
      "Visa service cancellation and refund eligibility depend on the stage of processing and the applicable service terms.",
      "Once an application has been submitted, processed, appointments completed or government/third-party fees paid, some or all charges may become non-refundable.",
      "Customers should check the applicable cancellation conditions before confirming the visa service.",
    ],
  },
  {
    number: 17,
    title: "Visa Service Delays",
    paragraphs: [
      "MakeBeeTrip shall not be responsible for delays caused by:",
    ],
    bullets: [
      "Embassy or consulate processing",
      "Immigration authorities",
      "Visa application centres",
      "Government holidays",
      "Additional verification",
      "Security checks",
      "Requests for additional documents",
      "Technical problems with government systems",
      "Changes in visa procedures",
      "Other circumstances outside our reasonable control",
    ],
  },
  {
    number: 18,
    title: "No Guarantee of Travel Date",
    paragraphs: [
      "Customers should not make non-refundable travel arrangements solely based on an estimated visa processing time unless they understand the associated risk.",
      "MakeBeeTrip cannot guarantee that a visa will be issued before a customer's intended travel date.",
    ],
  },
  {
    number: 19,
    title: "Third-Party Visa Partners",
    paragraphs: [
      "MakeBeeTrip may work with third-party visa processing partners or service providers to facilitate certain visa services.",
      "Where applicable, the terms and conditions of the relevant third-party service provider may also apply.",
    ],
  },
  {
    number: 20,
    title: "Customer Declaration",
    paragraphs: [
      "By submitting a visa request through MakeBeeTrip, the customer confirms that:",
    ],
    bullets: [
      "The information provided is accurate to the best of their knowledge.",
      "The documents submitted are genuine.",
      "They understand that visa approval is not guaranteed.",
      "They understand that processing times may vary.",
      "They understand that visa and government fees may be non-refundable.",
      "They will provide additional documents when requested.",
      "They are responsible for meeting the destination country's immigration requirements.",
    ],
  },
  {
    number: 21,
    title: "Contact Us",
    paragraphs: [
      "For visa-related enquiries and assistance, please contact:",
    ],
    contact: true,
  },
];

const TermsSection = ({ section }) => (
  <section
    id={`visa-section-${section.number}`}
    className="mbt-visa-section"
  >
    <div className="mbt-visa-section__heading">
      <span className="mbt-visa-section__number">
        {String(section.number).padStart(2, "0")}
      </span>

      <h2>{section.title}</h2>
    </div>

    <div className="mbt-visa-section__content">
      {section.paragraphs?.map((paragraph, index) => (
        <p key={`paragraph-${index}`}>{paragraph}</p>
      ))}

      {section.bullets && (
        <ul>
          {section.bullets.map((bullet, index) => (
            <li key={`bullet-${index}`}>{bullet}</li>
          ))}
        </ul>
      )}

      {section.contact && (
        <div className="mbt-visa-contact">
          <strong>MakeBeeTrip Private Limited</strong>

          <p>
            <b>Phone:</b>{" "}
            <a href="tel:9355471999">9355471999</a>
          </p>

          <p>
            <b>Email:</b>{" "}
            <a href="mailto:Holidays@makebeetrip.com">
              Holidays@makebeetrip.com
            </a>
          </p>

          <p>
            <b>Website:</b>{" "}
            <a
              href="https://makebeetrip.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://makebeetrip.com/
            </a>
          </p>

          <p>
            <b>Registered/Mailing Address:</b>
            <br />
            WZ A-1/1, G/F R/P, Vikas Puri, New Delhi, West Delhi – 110018,
            Delhi, India
          </p>

          <p>
            <b>CIN:</b> U79110DC2026PTC471178
          </p>
        </div>
      )}
    </div>
  </section>
);

const VisaTerms = () => {
  usePageMeta(
  "Visa Services Terms & Conditions | MakeBeeTrip",
  "Terms for visa assistance services on MakeBeeTrip: documentation, timelines, embassy decisions, fees and refunds.",
  "visa services terms, visa assistance terms, visa application terms, visa fees, embassy processing"
);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const documentHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      const progress =
        documentHeight > 0 ? (scrollTop / documentHeight) * 100 : 0;

      setScrollProgress(Math.min(progress, 100));
      setShowScrollTop(scrollTop > 500);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <main className="mbt-visa-terms">
      <div
        className="mbt-visa-scroll-progress"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      <nav className="mbt-visa-sticky-nav">
        <div className="mbt-visa-nav-container">
          <button
  type="button"
  className="mbt-visa-back-button"
  onClick={() => window.history.back()}
>
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
  >
    <path
      d="M15 18L9 12L15 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>

  <span>Back</span>
</button>

          <span className="mbt-visa-nav-label">
            Visa Disclaimer &amp; Visa Terms
          </span>
        </div>
      </nav>

      <header className="mbt-visa-hero">
        <div className="mbt-visa-hero__container">
          <div className="mbt-visa-icon" aria-hidden="true">
  <svg
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="16"
      y="8"
      width="32"
      height="48"
      rx="5"
      stroke="currentColor"
      strokeWidth="3"
    />

    <circle
      cx="32"
      cy="23"
      r="6"
      stroke="currentColor"
      strokeWidth="2.5"
    />

    <path
      d="M23 39C25.5 34.5 29 32.5 32 32.5C35 32.5 38.5 34.5 41 39"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />

    <path
      d="M23 46H41"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
</div>

          <span className="mbt-visa-eyebrow">
  VISA SERVICE TERMS
</span>

<h1>Visa Disclaimer &amp; Visa Terms</h1>

<p className="mbt-visa-hero__description">
  Terms and conditions applicable to visa assistance and documentation
  support provided through MakeBeeTrip Private Limited.
</p>

<div className="mbt-visa-updated-badge">
  Last Updated: 24 September 2026
</div>
        </div>
      </header>

      <div className="mbt-visa-content-container">
        <article className="mbt-visa-card">
          <div className="mbt-visa-document-intro">
            <p>
              MakeBeeTrip Private Limited provides{" "}
              <strong>visa assistance and documentation support</strong> for
              selected destinations. Visa services are subject to the
              requirements, rules and decisions of the relevant embassy,
              consulate, immigration authority, visa centre or other authorized
              government authority.
            </p>

            <p>
              By requesting visa assistance through{" "}
              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://makebeetrip.com/
              </a>
              , you acknowledge and agree to the following Visa Disclaimer &
              Visa Terms.
            </p>
          </div>

          <div
            className="mbt-visa-quick-nav"
            aria-label="Visa terms sections"
          >
            <div className="mbt-visa-quick-nav__header">
              <span className="mbt-visa-quick-nav__eyebrow">
                QUICK NAVIGATION
              </span>

              <h2>Visa Terms</h2>

              <p>
                Jump directly to any section of the Visa Disclaimer &amp; Visa
                Terms.
              </p>
            </div>

            <div className="mbt-visa-quick-nav__links">
              {visaTerms.map((section) => (
                <a
                  key={section.number}
                  href={`#visa-section-${section.number}`}
                >
                  <span>
                    {String(section.number).padStart(2, "0")}
                  </span>

                  {section.title}
                </a>
              ))}

              <a href="#visa-important-notice">
                <span>!</span>
                Important Visa Notice
              </a>
            </div>
          </div>

          <div className="mbt-visa-sections">
            {visaTerms.map((section, index) => (
              <React.Fragment key={section.number}>
                <TermsSection section={section} />

                {index < visaTerms.length - 1 && (
                  <hr className="mbt-visa-divider" />
                )}
              </React.Fragment>
            ))}
          </div>

          <hr className="mbt-visa-divider" />

          <section
            id="visa-important-notice"
            className="mbt-visa-important-notice"
          >
            <div
              className="mbt-visa-important-notice__icon"
              aria-hidden="true"
            >
              !
            </div>

            <div>
              <span>IMPORTANT VISA NOTICE</span>

              <p>
                MakeBeeTrip provides visa assistance and does not have
                authority to issue or approve visas. Visa approval, processing
                and entry decisions are solely made by the relevant government,
                embassy, consulate, immigration authority or authorized visa
                service provider.
              </p>
            </div>
          </section>

          <footer className="mbt-visa-footer">
            <div className="mbt-visa-footer__brand">
              MakeBeeTrip – Your Journey, Our Commitment.
            </div>

            <div className="mbt-visa-footer__contact">
              <a href="tel:9355471999">9355471999</a>

              <a href="mailto:Holidays@makebeetrip.com">
                Holidays@makebeetrip.com
              </a>

              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                makebeetrip.com
              </a>
            </div>

            <div className="mbt-visa-footer__copyright">
              © {new Date().getFullYear()} MakeBeeTrip Private Limited. All
              rights reserved.
            </div>
          </footer>
        </article>
      </div>

      {showScrollTop && (
        <button
          type="button"
          className="mbt-visa-scroll-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </main>
  );
};

export default VisaTerms;